import React from "react";
import { useEffect, useMemo, useState } from "react";
import { CacaoPointComponent } from "../Toolbar/CacaoPoint";
import Tooltip from "../DesignSystem/Tooltip";
import { AvatarList } from "../DesignSystem/AvatarList";
import { RLink } from "../DesignSystem/RLink";
import { api } from "../api";

type CacaoRoundProps = {
  roundType: "Normal" | "Bonus";
  final?: boolean;
  roundCount: number;
  roundId?: number;
  discussionId?: number;
  start: Date;
  end: Date;
};

const CacaoRounds: CacaoRoundProps[] = [
  {
    roundType: "Normal",
    roundCount: 1,
    roundId: 44,
    discussionId: 39,
    start: new Date("2018-03-02T23:00:00Z"),
    end: new Date("2018-03-11T23:00:00Z"),
  },
  {
    roundType: "Bonus",
    roundCount: 1,
    roundId: 48,
    start: new Date("2018-03-11T23:00:00Z"),
    end: new Date("2018-03-16T23:00:00Z"),
  },
  {
    roundType: "Normal",
    roundCount: 2,
    roundId: 49,
    start: new Date("2018-03-17T23:00:00Z"),
    end: new Date("2018-03-25T23:00:00Z"),
  },
  {
    roundType: "Bonus",
    roundCount: 2,
    roundId: 50,
    start: new Date("2018-03-25T23:00:00Z"),
    end: new Date("2018-03-30T23:00:00Z"),
  },
  {
    roundType: "Normal",
    roundCount: 3,
    roundId: 51,
    start: new Date("2018-03-30T23:00:00Z"),
    end: new Date("2018-04-08T23:00:00Z"),
  },
  {
    roundType: "Bonus",
    roundCount: 3,
    roundId: 52,
    start: new Date("2018-04-08T23:00:00Z"),
    end: new Date("2018-04-13T23:00:00Z"),
  },
  {
    roundType: "Bonus",
    roundCount: 4,
    final: true,
    start: new Date("2018-04-16T23:00:00Z"),
    end: new Date("2018-04-22:00:00Z"),
  },
  {
    roundType: "Normal",
    roundCount: 4,
    final: true,
    start: new Date("2018-04-22:00:00Z"),
    end: new Date("2018-05-05T23:00:00Z"),
  },
];

const formatDate = (date: Date): string => {
  const monthNames = [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
  ];

  return `${monthNames[date.getMonth()]} ${date.getDate()}`;
};

const CacaoRound = (props: CacaoRoundProps): JSX.Element => {
  const started = props.start < new Date();

  let roundElement: JSX.Element = (
    <span>
      {props.final && "Final"} {props.final ? (props.roundType === "Bonus" ? "Bonus" : "") : props.roundType} Round{" "}
      {!props.final && props.roundCount}
    </span>
  );

  if (props.final && props.roundType === "Bonus") {
    roundElement = <span>~ Special Round ~</span>;
  }

  if (props.roundId && started) {
    roundElement = <RLink to={`/compo/${props.roundId}`}>{roundElement}</RLink>;
  }

  let currentState: JSX.Element | undefined;

  if (!started) {
    currentState = <span>This round has yet to start.</span>;
  } else if (props.end > new Date() && props.start < new Date()) {
    currentState = <span className="text-red-600">This round is going on right now!</span>;
  } else if (props.end < new Date()) {
    currentState = <span>This round is finished.</span>;
  }

  return (
    <div className="m-[10px] border border-gray-300 p-[10px]">
      <div className="text-lg text-gray-500">
        <div>
          {props.roundType === "Bonus" && (
            <Tooltip
              element={<div className="m-0 text-[40px] font-light">{roundElement}</div>}
              tooltip={
                props.final ? (
                  <div>
                    <div className="mb-[10px]">???</div>
                  </div>
                ) : (
                  <div>
                    <div className="mb-[10px]">
                      Do something stupid and get 10 <CacaoPointComponent />.
                    </div>
                    (What you have to do will be announced when the bonus contest starts.)
                  </div>
                )
              }
            />
          )}
          {props.roundType === "Normal" && (
            <Tooltip
              element={<div className="m-0 text-[40px] font-light">{roundElement}</div>}
              tooltip={
                <div>
                  <div className="mb-[10px]">
                    Write an original song in {props.final ? "two weeks" : "a week"}. The top ranked songs will be
                    awarded points like so.
                  </div>

                  <div>
                    1st <span className="float-right">100 <CacaoPointComponent /></span>
                  </div>
                  <div>
                    2nd: <span className="float-right">90 <CacaoPointComponent /> </span>
                  </div>
                  <div>
                    3rd: <span className="float-right">80 <CacaoPointComponent /> </span>
                  </div>
                  <div>
                    4th: <span className="float-right">70 <CacaoPointComponent /> </span>
                  </div>
                  <div>
                    5th: <span className="float-right">60 <CacaoPointComponent /> </span>
                  </div>
                  <div>
                    6th: <span className="float-right">50 <CacaoPointComponent /> </span>
                  </div>
                  <div>
                    7th: <span className="float-right">40 <CacaoPointComponent /> </span>
                  </div>
                  <div>
                    8th: <span className="float-right">30 <CacaoPointComponent /> </span>
                  </div>
                  <div>
                    9th: <span className="float-right">20 <CacaoPointComponent /> </span>
                  </div>
                  <div>
                    10th: <span className="float-right">10 <CacaoPointComponent /> </span>
                  </div>
                  <div>
                    11-15: <span className="float-right">5 <CacaoPointComponent /> </span>
                  </div>
                </div>
              }
            />
          )}
          {currentState}{" "}
          {props.discussionId && (
            <RLink to={`/forum/topic/${props.discussionId}`}>
              Forum post.
            </RLink>
          )}
        </div>
        {formatDate(props.start)} - {formatDate(props.end)}
      </div>
    </div>
  );
};

type CacaoParticipant = {
  amount: number;
  avatar_url: string;
  username: string;
  changes: number[];
  reason: string[];
};

type CacaoCardProps = CacaoParticipant & { rank: number };

const CacaoCard = ({ amount, username, changes, reason, rank, avatar_url }: CacaoCardProps): JSX.Element => {
  const [expanded, setExpanded] = useState(false);

  if (!expanded) {
    return (
      <tr>
        <td> {rank} </td>
        <td>
          <AvatarList
            users={[
              {
                username,
                avatar_url,
              },
            ]}
          />
        </td>
        <td>
          <CacaoPointComponent /> {amount}
        </td>
        <td>
          <RLink to="javascript:;" onClick={() => setExpanded(!expanded)}>
            breakdown
          </RLink>
        </td>
      </tr>
    );
  }

  return (
    <tbody className="text-gray-500">
      <tr>
        <td>{rank}</td>
        <td>
          <AvatarList
            users={[
              {
                username,
                avatar_url,
              },
            ]}
          />
          {username}
        </td>
      </tr>
      {reason.map((_, index) => (
        <tr key={`${username}-reason-${index}`}>
          <td></td>
          <td>{reason[index]}</td>
          <td>
            <CacaoPointComponent /> {changes[index]}
          </td>
        </tr>
      ))}
      <tr>
        <td></td>
        <td>
          <RLink to="javascript:;" onClick={() => setExpanded(!expanded)}>
            hide
          </RLink>
        </td>
      </tr>
    </tbody>
  );
};

export const ChipsCacaophonyPage = (): JSX.Element => {
  const [cacaoParticipants, setCacaoParticipants] = useState<CacaoParticipant[]>([]);

  useEffect(() => {
    let cancelled = false;
    const fetchParticipants = async () => {
      const participants = await api.get("/api/cacao/participants");
      if (!cancelled) {
        setCacaoParticipants(participants);
      }
    };
    fetchParticipants();
    return () => {
      cancelled = true;
    };
  }, []);

  const { normalCacaoRounds, finalCacaoRounds } = useMemo(() => {
    const normal = CacaoRounds.filter((round) => !round.final || (round.final && round.roundType === "Bonus"));
    const finals = CacaoRounds.filter((round) => round.final && round.roundType !== "Bonus");
    return { normalCacaoRounds: normal, finalCacaoRounds: finals };
  }, []);

  let lastRank = 0;

  return (
    <div className="px-[15%] py-[50px] bg-[#f7f7f9] pt-[100px]">
      <div className="pb-[20px] text-center text-[50px] font-normal">Chips Cacaophony</div>

      <div className="pl-[5%] pr-[5%] pb-[50px]">
        <div className="inline-block w-[60%] align-top">
          <div className="p-[10px] text-[18px] text-gray-500">
            Chips Cacaophony is a multi-round original music competition. Rank high enough to score 100{" "}
            <CacaoPointComponent /> in the first rounds, and you'll qualify to the final rounds and hopefully win!
          </div>

          <div>
            Hang out with other cacao-ers in our <RLink to="https://discord.gg/AUvDuUkxXr">discord!</RLink>
          </div>

          <div>
            {normalCacaoRounds.map((round) => (
              <CacaoRound key={`${round.roundType}-${round.roundCount}`} {...round} />
            ))}

            <div className="py-[10px] text-center text-[24px]">
              <div className="flex items-center justify-center gap-4">
                <span aria-hidden="true" className="flex-1 border-b border-gray-300"></span>
                <span className="whitespace-nowrap">
                  100 <CacaoPointComponent /> required
                </span>
                <span aria-hidden="true" className="flex-1 border-b border-gray-300"></span>
              </div>
            </div>

            {finalCacaoRounds.map((round) => (
              <CacaoRound key={`${round.roundType}-${round.roundCount}-final`} {...round} />
            ))}
          </div>

          <div className="p-[10px] text-[18px] text-gray-500">
            <RLink to="/cacaophony/explain">Read the full details of the contest.</RLink>
          </div>
        </div>

        <div className="inline-block align-top w-[35%] ml-[3%]">
          <div className="p-[5px] text-[30px] font-extralight uppercase tracking-[3px] text-black">Leaderboard</div>

          <table>
            {cacaoParticipants.map((participant, index) => {
              const rank =
                index === 0
                  ? 0
                  : cacaoParticipants[index].amount === cacaoParticipants[index - 1].amount
                  ? lastRank
                  : index;

              lastRank = rank;

              return <CacaoCard key={`${participant.username}-${index}`} {...participant} rank={rank + 1} />;
            })}
          </table>
        </div>
      </div>
    </div>
  );
};
